import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import type { OrgMember } from '@cotera/client/app/etc/api/typed-client';

/**
 * Read-only roster of the current org's members, indexed by user id. Used to
 * attribute resources to their owner (e.g. "from Sarah" on Shared with me).
 *
 * Owners outside the current org (cross-org external shares) won't appear —
 * callers should treat a missing entry as "unknown owner" and degrade
 * gracefully (show no attribution) rather than assume presence.
 */
export function useOrgMembersById(): Map<string, OrgMember> {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);

  const { data } = useQuery({
    queryKey: ['org', 'members', orgId],
    queryFn: async () => Assert.assertOk(await client.org.listMembers({})),
    staleTime: 5 * 60_000,
  });

  return useMemo(() => {
    const byId = new Map<string, OrgMember>();
    for (const member of data?.members ?? []) {
      byId.set(member.id, member);
    }
    return byId;
  }, [data]);
}

/** First-initial(s) for an avatar fallback, from a name or email. */
export function memberInitials(nameOrEmail: string): string {
  const trimmed = nameOrEmail.trim();
  if (trimmed === '') {
    return 'U';
  }
  const parts = trimmed.split(/\s+/).filter((p) => p.length > 0);
  if (parts.length >= 2) {
    return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
  }
  return trimmed.slice(0, 1).toUpperCase();
}

/** Warm theme palette (v1.css `--chart-1..10`) used to fill avatars. */
const AVATAR_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
  'var(--chart-9)',
  'var(--chart-10)',
];

/**
 * Deterministic theme color for a person's avatar, keyed to a stable seed (the
 * member id). Keying to the person — not just their first initial — means two
 * people who share a letter still get different colors, which is the whole
 * point: telling avatars apart at a glance.
 */
export function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const len = AVATAR_COLORS.length;
  return AVATAR_COLORS[((hash % len) + len) % len]!;
}
